import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import styles from './CodeBlock.module.css'
import {getLanguageInfo} from '@github-ui/copilot-chat/utils/language-info'
import {useContext, type PropsWithChildren, useState} from 'react'
import {LanguageDot} from './LanguageDot'
import {ExtensionContext} from '../ExtensionContext'
import {clsx} from 'clsx'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useFilteredCopilotAnnotations} from '@github-ui/copilot-chat/hooks/use-filtered-copilot-annotations'
import {IconButton} from '@primer/react'
import {ShieldIcon} from '@primer/octicons-react'

import {CodeInsightsDialog} from './CodeInsightsDialog/CodeInsightsDialog'

export interface CodeBlockProps {
  language: string
  code: string
  startOffset: number
  endOffset: number
}

export function CodeBlock({language, children, code, startOffset, endOffset}: PropsWithChildren<CodeBlockProps>) {
  const {color, name: displayName} = getLanguageInfo(language)

  const extensionContext = useContext(ExtensionContext)

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const {publicCodeReferences, codeVulnerabilities} = useFilteredCopilotAnnotations(
    extensionContext.copilotAnnotations,
    startOffset,
    endOffset,
  )

  return (
    <>
      <figure
        className={clsx(styles.container, {
          [styles.immersive]: extensionContext.chatMode === 'immersive',
          [styles.assistive]: extensionContext.chatMode === 'assistive',
        })}
      >
        <figcaption className={styles.header}>
          <LanguageDot color={color} />
          <span className={styles.languageName}>{displayName || 'Code'}</span>
          {(publicCodeReferences.length > 0 || codeVulnerabilities.length > 0) && (
            <div className={styles.insightsButtonContainer}>
              <IconButton
                variant="invisible"
                size="small"
                icon={ShieldIcon}
                aria-label="Code insights"
                onClick={() => setIsDialogOpen(true)}
              />
            </div>
          )}
        </figcaption>
        <div className={styles.copyContainer}>
          <div className={styles.copyContent}>
            <CopyToClipboardButton
              textToCopy={code}
              ariaLabel="Copy code"
              className={styles.copyButton}
              size="small"
              onCopy={() => {
                sendEvent('dotcom_chat.activate', {
                  target: 'CODE_BLOCK_COPY',
                  mode: extensionContext.chatMode,
                })
              }}
            />
          </div>
        </div>
        <div className={styles.codeContainer}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex*/}
          <pre className={styles.code} tabIndex={0}>
            <code>{children}</code>
          </pre>
        </div>
      </figure>
      {isDialogOpen && (
        <CodeInsightsDialog
          publicCodeReferences={publicCodeReferences}
          codeVulnerabilities={codeVulnerabilities}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  )
}

try{ CodeBlock.displayName ||= 'CodeBlock' } catch {}